import { useEffect, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Toast } from '@repo/videa-components/src/index';
import { toast } from 'sonner';

import { FeedbackView } from './dialogViews/FeedbackView';
import type { FeedbackOption } from './types';

interface FeedbackDialogProps {
  rating: 'positive' | 'negative' | null;
  positiveOptions: FeedbackOption[];
  negativeOptions: FeedbackOption[];
  initialSelectedOptions?: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onRate: (
    rating: 'positive' | 'negative',
    selectedOptions: string[],
    additionalComments?: string,
  ) => void;
}

export const FeedbackDialog = ({
  rating,
  positiveOptions,
  negativeOptions,
  initialSelectedOptions = [],
  isOpen,
  onOpenChange,
  onRate,
}: FeedbackDialogProps) => {
  // state
  const [selectedFeedbackOptions, setSelectedFeedbackOptions] = useState<
    string[]
  >([]);
  const [additionalComments, setAdditionalComments] = useState<string>('');

  // hooks
  const { t } = useLingui();

  // handlers
  const handleSubmitFeedback = () => {
    if (rating) {
      onRate(rating, selectedFeedbackOptions, additionalComments);
    }

    // Show success toast
    toast(
      <Toast
        variant="success"
        title={t`Feedback sent!`}
        description={t`Thanks for helping us improve the product.`}
      />,
      {
        closeButton: true,
        position: 'top-right',
        style: { width: '460px' },
      },
    );

    onOpenChange(false);
  };

  const handleToggleOption = (value: string) => {
    if (selectedFeedbackOptions.includes(value)) {
      setSelectedFeedbackOptions(
        selectedFeedbackOptions.filter((item) => item !== value),
      );
    } else {
      setSelectedFeedbackOptions([...selectedFeedbackOptions, value]);
    }
  };

  const handleCommentsChange = (comments: string) => {
    setAdditionalComments(comments);
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFeedbackOptions(initialSelectedOptions);
      setAdditionalComments('');
    }
  }, [isOpen, initialSelectedOptions]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="vai-max-w-[538px]"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="vai-text-xl vai-font-semibold">
            <Trans>Tell us more:</Trans>
          </DialogTitle>
        </DialogHeader>

        <FeedbackView
          rating={rating}
          positiveOptions={positiveOptions}
          negativeOptions={negativeOptions}
          selectedFeedbackOptions={selectedFeedbackOptions}
          additionalComments={additionalComments}
          onToggleOption={handleToggleOption}
          onCommentsChange={handleCommentsChange}
          onSubmit={handleSubmitFeedback}
        />
      </DialogContent>
    </Dialog>
  );
};
